import { useMemo, useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, ListTodo } from 'lucide-react'
import type { BatchStarted, Issue, IssueList, JobCreated, TicketTriage } from '@one-deployment/api'
import { Button, Input, PageHeader, Segmented, SkeletonRows } from '@/components/ui'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Age, EmptyState, ErrorState, StageBadge } from '@/components/status'
import { RoleGate, useCan } from '@/components/RoleGate'
import { enc, get, post } from '@/lib/api'
import { useLiveTopic } from '@/lib/live'
import { useProject } from '@/features/projects/Overview'
import { qk } from '@/lib/query'
import { useLayout } from '@/stores/layout'
import { AccountPicker } from '@/features/claude/AccountPicker'
import { TriageChips, TriageMenu } from './Triage'

const ORDER = ['triage', 'unstarted', 'started', 'backlog', 'completed', 'canceled']
const PRIORITY = ['—', 'Urgent', 'High', 'Medium', 'Low']
type Kind = 'all' | TicketTriage['kind'] | 'held'
const KINDS: { value: Kind; label: string }[] = [
  { value: 'all', label: 'All' }, { value: 'bug', label: 'Bugs' }, { value: 'feature', label: 'Features' },
  { value: 'change_request', label: 'Changes' }, { value: 'error', label: 'Errors' }, { value: 'held', label: 'Held' },
]
/** Can be picked for a batch: not held, and nothing running or finished on it. */
const startable = (i: Issue) => !i.triage?.held && (!i.stage || ['triaged', 'declined', 'needs_human'].includes(i.stage))

/** Linear issues, read from the mirror (03 §5.7). Delegate = hand to the agent. */
export function IssuesPage() {
  const { project } = useParams({ strict: false }) as { project: string }
  const [q, setQ] = useState('')
  const view = useLayout((s) => s.boardView)
  const setView = useLayout((s) => s.setBoardView)
  const qc = useQueryClient()
  const [accountId, setAccountId] = useState<string | null>(null)
  useLiveTopic(`project:${project}`)
  const proj = useProject(project)
  // The mirror has no live topic of its own: a minute is how stale a Linear edit can look here.
  const list = useQuery({
    queryKey: [...qk.issues(project), q],
    queryFn: ({ signal }) => get<IssueList>(`/api/projects/${enc(project)}/issues${q ? `?q=${enc(q)}` : ''}`, signal),
    refetchInterval: 60_000,
  })
  const { error, refetch } = list
  const data = list.data?.issues
  const [kind, setKind] = useState<Kind>('all')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const canStart = useCan('job.delegate', project)
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const batch = useMutation({
    mutationFn: (identifiers: string[]) => post<BatchStarted>(`/api/projects/${enc(project)}/issues/start`, { identifiers, ...(accountId ? { accountId } : {}) }),
    onSuccess: () => setPicked(new Set()),
    onSettled: () => { void qc.invalidateQueries({ queryKey: qk.issues(project) }); void qc.invalidateQueries({ queryKey: qk.board(project) }) },
  })
  const delegate = useMutation({
    mutationFn: (identifier: string) => post<JobCreated>(`/api/projects/${enc(project)}/issues/${enc(identifier)}/delegate`, { mode: 'build', ...(accountId ? { accountId } : {}) }),
    onSettled: () => { void qc.invalidateQueries({ queryKey: qk.issues(project) }); void qc.invalidateQueries({ queryKey: qk.board(project) }); void qc.invalidateQueries({ queryKey: qk.home() }) },
  })

  const groups = useMemo(() => {
    const by = new Map<string, Issue[]>()
    const rank = (i: Issue) => { const n = ORDER.indexOf(i.stateType ?? ''); return n === -1 ? ORDER.length : n }
    const shown = (data ?? []).filter((i) => kind === 'all' || (kind === 'held' ? i.triage?.held : i.triage?.kind === kind))
    for (const i of [...shown].sort((a, b) => rank(a) - rank(b))) {
      const state = i.stateName ?? 'No state'
      const g = by.get(state) ?? []
      g.push(i)
      by.set(state, g)
    }
    return [...by.entries()]
  }, [data, kind])

  const linked = !!proj.data?.linearTeamId
  const synced = list.data?.lastSyncedAt
  const row = (i: Issue, compact = false) => (
    <div key={i.id} className={compact ? 'mb-1.5 rounded-md border border-border bg-surface-2 p-2 text-sm' : 'flex h-8 items-center gap-3 border-b border-border px-5 text-sm hover:bg-hover'}>
      {!compact && canStart && (
        <Checkbox aria-label={`Pick ${i.identifier}`} className="size-3.5 disabled:opacity-30" disabled={!startable(i)}
          checked={picked.has(i.identifier)} onCheckedChange={() => toggle(i.identifier)} title={startable(i) ? 'Pick for a batch start' : i.triage?.held ? 'Held: out of scope' : 'Already running or done'} />
      )}
      <span className="w-20 shrink-0 font-mono text-xs text-fg-subtle">{i.identifier}</span>
      <span className={compact ? 'my-1 block' : 'flex-1 truncate'}>{i.title}</span>
      <span className="flex items-center gap-2">
        {!compact && <span className="w-14 text-xs text-fg-faint">{PRIORITY[i.priority ?? 0] ?? '—'}</span>}
        {!compact && <span className="w-24 truncate text-xs text-fg-subtle">{i.assignee ?? 'unassigned'}</span>}
        {i.triage && <TriageChips t={i.triage} />}
        {i.stage && i.stage !== 'triaged' ? (
          <Link to="/p/$project/w/$ticket" params={{ project, ticket: i.identifier }}><StageBadge stage={i.stage} /></Link>
        ) : i.triage?.held ? null : (
          <RoleGate action="job.delegate" project={project}>
            <Button size="sm" disabled={delegate.isPending || proj.data?.archived} title="Hand this ticket to the agent" onClick={() => delegate.mutate(i.identifier)}>Delegate</Button>
          </RoleGate>
        )}
        <TriageMenu project={project} i={i} />
        <a href={i.url} target="_blank" rel="noopener noreferrer" aria-label="Open in Linear" className="text-fg-faint hover:text-fg"><ExternalLink size={12} /></a>
      </span>
    </div>
  )

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Issues"
        meta={linked ? <>Linear mirror synced {synced ? <><Age from={synced} prefix="" /> ago</> : 'never'}</> : 'No Linear team connected'}
        actions={<><Link to="/p/$project/intake" params={{ project }} className="text-xs text-accent hover:underline">Tickets from text</Link><AccountPicker value={accountId} onChange={setAccountId} label="Claude account for tickets delegated here" /><Input placeholder="Filter…" value={q} onChange={(e) => setQ(e.target.value)} className="h-7 w-56 text-xs" /><Segmented value={view} onChange={setView} options={[{ value: 'list', label: 'List' }, { value: 'board', label: 'Board' }]} /></>}
      />
      <div className="flex items-center gap-3 border-b border-border px-5 py-2 text-xs">
        <Segmented value={kind} onChange={setKind} options={KINDS} />
        {picked.size > 0 && (
          <>
            <Button variant="primary" size="sm" disabled={batch.isPending} onClick={() => batch.mutate([...picked])}>Start {picked.size} ticket{picked.size === 1 ? '' : 's'}</Button>
            <Button variant="ghost" className="h-6 px-1.5 text-fg-subtle hover:text-fg" onClick={() => setPicked(new Set())}>Clear</Button>
            <span className="text-fg-faint">Each gets its own session and PR. Tickets in the same area run one at a time.</span>
          </>
        )}
        {batch.data && (
          <span className="text-fg-subtle">Started {batch.data.started.length}{batch.data.skipped.length ? `; skipped ${batch.data.skipped.map((s) => `${s.identifier} (${s.reason})`).join(', ')}` : ''}</span>
        )}
      </div>
      {(delegate.error ?? batch.error) && <div className="px-5 pt-3"><ErrorState error={delegate.error ?? batch.error} compact /></div>}
      <div className="min-h-0 flex-1 overflow-auto">
        {proj.data && !linked ? (
          <EmptyState icon={<ListTodo size={20} />} title="No Linear team connected" body="Connect one in project settings to see and delegate issues here." action={<Link to="/p/$project/settings/$section" params={{ project, section: 'linear' }}><Button variant="primary">Open Linear settings</Button></Link>} />
        ) : error && !data ? <ErrorState error={error} retry={() => void refetch()} /> : !data ? <SkeletonRows rows={10} /> : data.length === 0 ? (
          <EmptyState icon={<ListTodo size={20} />} title={q ? 'No issues match' : 'No open issues'} body={q ? 'Try a different filter.' : 'The mirror is empty for this team. New Linear issues appear within seconds.'} />
        ) : view === 'board' ? (
          <div className="flex h-full gap-3 overflow-x-auto p-4">
            {groups.map(([state, items]) => (
              <div key={state} className="w-72 shrink-0">
                <div className="mb-2 flex justify-between text-xs text-fg-subtle"><span>{state}</span><span>{items.length}</span></div>
                {items.map((i) => row(i, true))}
              </div>
            ))}
          </div>
        ) : groups.map(([state, items]) => (
          <section key={state}>
            <div className="sticky top-0 flex h-7 items-center justify-between border-b border-border bg-surface-1 px-5 text-xs text-fg-subtle"><span>{state}</span><span>{items.length}</span></div>
            {items.map((i) => row(i))}
          </section>
        ))}
      </div>
    </div>
  )
}
